/**
 * Zod schemas for the Formulator toolset (epic PI-SPEC-SW-E282, contract
 * formulator 1; formulator 2, epic PI-SPEC-SW-E283, adds the optional
 * `persona` on `turn.json` and the interview view; formulator 3, epic
 * PI-SPEC-SW-E321, adds the optional Claude `draft` on the engine turn and
 * marker, the `purpose`, `draftBasis`, `currentDraft` and
 * `redraftInstruction` on `turn.json`, and the optional `paths` on a
 * finding. Every formulator 3 addition is optional, so a contract-2 engine
 * and marker stay valid; formulator 4, epic PI-SPEC-SW-E322, adds the
 * optional `inferred` part keys on the engine turn and marker, `confirmed`
 * on slot-guide parts and `confirmInferred` on `turn.json` and the view, all
 * optional, so a contract-3 engine and marker stay valid).
 *
 * The Formulator interview runs on the person's own Claude Code seat inside
 * SpecTree Toolbox. SpecTree keeps the session, scores it with the readiness
 * rubric and writes to the tracker; it makes no AI call for this flow. One
 * turn travels like this:
 *
 * 1. The engine writes `turn.json` ({@link formulatorTurnInputSchema}) and
 *    runs the internal plugin skill with `claude -p`.
 * 2. The skill reads the code and prints one line:
 *    `SPECTREE_FORMULATOR_TURN {json}` ({@link formulatorTurnMarkerSchema}).
 * 3. The engine posts the turn ({@link formulatorTurnBodySchema}) to
 *    `POST /api/v1/work-items/interview/:sessionId/reply`.
 * 4. Pages and the engine read the session back
 *    ({@link formulatorInterviewViewSchema}).
 *
 * Covered routes, under `/api/v1/work-items`:
 * - `POST /interview/start`                   start a session
 * - `GET  /interview`                         the caller's unfinished sessions
 * - `GET  /interview/:sessionId`              one session
 * - `POST /interview/:sessionId/reply`        the engine turn
 * - `GET  /interview/:sessionId/review`       the review before writing
 * - `POST /interview/:sessionId/submit`       write to the tracker
 * - `GET  /:tracker/:container/open-items`    duplicate candidates
 *
 * Request schemas are strict (unknown keys are refused). Response schemas
 * are lenient, so a SpecTree release may add response fields without a
 * contract bump. Every response is wrapped as `{ data: ... }`; parse the
 * envelope with `apiDataSchema` from the grooming module.
 */
import { z } from "zod";
import { boundedRecord, isoDateTime } from "../zod-helpers.js";
import { MAX_GROOMING_REPOS } from "../grooming/constants.js";
import { TOOLBOX_PERSONAS } from "../roles/types.js";
import { FORMULATOR_AREA_NAME_MAX, FORMULATOR_AREAS_MAX, FORMULATOR_AREAS_NOTE_MAX, FORMULATOR_DESCRIPTION_MAX, FORMULATOR_DRAFT_ITEM_MAX, FORMULATOR_DRAFT_ITEMS_MAX, FORMULATOR_DRAFT_TEXT_MAX, FORMULATOR_DUPLICATE_REASON_MAX, FORMULATOR_DUPLICATE_TITLE_MAX, FORMULATOR_DUPLICATES_MAX, FORMULATOR_EXTRACTED_KEY_MAX, FORMULATOR_EXTRACTED_KEYS_MAX, FORMULATOR_EXTRACTED_VALUE_MAX, FORMULATOR_FINDING_NAME_MAX, FORMULATOR_FINDING_PATH_MAX, FORMULATOR_FINDING_PATHS_MAX, FORMULATOR_FINDING_WHY_MAX, FORMULATOR_FINDINGS_MAX, FORMULATOR_QUESTION_MAX, FORMULATOR_QUICK_PICK_MAX, FORMULATOR_QUICK_PICKS_MAX, FORMULATOR_REDRAFT_INSTRUCTION_MAX, FORMULATOR_REPLY_MAX, FORMULATOR_TRANSCRIPT_MAX, FORMULATOR_TURN_INPUT_SCHEMA_VERSION, FORMULATOR_TURN_PURPOSES, } from "./constants.js";
import { FORMULATOR_AREA_LEVELS, FORMULATOR_AWAITING, FORMULATOR_KINDS, FORMULATOR_MESSAGE_ROLES, FORMULATOR_MODES, FORMULATOR_SESSION_STATUSES, } from "./types.js";
// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------
/** Path prefix of the Formulator interview routes. */
export const FORMULATOR_API_PREFIX = "/api/v1/work-items/interview";
const WORK_ITEMS_PREFIX = "/api/v1/work-items";
/** Paths of the routes this module covers, relative to the SpecTree origin. */
export const formulatorApiPaths = {
    start: `${FORMULATOR_API_PREFIX}/start`,
    list: FORMULATOR_API_PREFIX,
    session: (sessionId) => `${FORMULATOR_API_PREFIX}/${encodeURIComponent(sessionId)}`,
    reply: (sessionId) => `${FORMULATOR_API_PREFIX}/${encodeURIComponent(sessionId)}/reply`,
    review: (sessionId) => `${FORMULATOR_API_PREFIX}/${encodeURIComponent(sessionId)}/review`,
    submit: (sessionId) => `${FORMULATOR_API_PREFIX}/${encodeURIComponent(sessionId)}/submit`,
    /** Open bugs and stories of one project; `kind` narrows to one kind. */
    openItems: (tracker, container, kind) => {
        const base = `${WORK_ITEMS_PREFIX}/${encodeURIComponent(tracker)}/${encodeURIComponent(container)}/open-items`;
        return kind === undefined ? base : `${base}?kind=${encodeURIComponent(kind)}`;
    },
};
// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------
/** An ISO 8601 timestamp as JSON carries a Date. */
const isoDateTimeSchema = isoDateTime({ offset: true });
/** An ADO work item id as a string: a positive integer of at most ten digits. */
export const formulatorItemIdSchema = z.string().regex(/^[1-9]\d{0,9}$/, "item ids must be positive integers");
/**
 * True when `value` is an absolute path on macOS, Linux or Windows
 * (`/x`, `C:\x`, `C:/x` or `\\server\share`). Browser-safe: no `node:path`.
 */
export function isAbsoluteFilePath(value) {
    if (value.includes("\0"))
        return false;
    return value.startsWith("/") || /^[A-Za-z]:[\\/]/.test(value) || /^\\\\[^\\]+\\/.test(value);
}
/** An absolute file-system path on the person's computer. */
export const absoluteFilePathSchema = z
    .string()
    .min(1)
    .max(1024)
    .refine(isAbsoluteFilePath, { message: "path must be absolute" });
export const formulatorKindSchema = z.enum(FORMULATOR_KINDS);
export const formulatorModeSchema = z.enum(FORMULATOR_MODES);
export const formulatorAwaitingSchema = z.enum(FORMULATOR_AWAITING);
export const formulatorSessionStatusSchema = z.enum(FORMULATOR_SESSION_STATUSES);
/**
 * True when `value` is a path relative to a repository root, safe to save as
 * a finding's path and to open later inside a repository copy: not empty, no
 * control character, not absolute (`/x`, `C:\x`, `\\server\share`), and no
 * `..` or `.` segment. Either separator is accepted (Grep reports `\` on
 * Windows). The same rule as `isRepoRelativePath` in `fogbugz/code.ts`, plus
 * the control-character check; kept here so `formulator` imports nothing from
 * `fogbugz`, which already imports from this module.
 */
export function isFormulatorFindingPath(value) {
    if (value.length === 0 || /\p{Cc}/u.test(value))
        return false;
    if (/^[\\/]/.test(value) || /^[A-Za-z]:/.test(value))
        return false;
    return value.split(/[\\/]/).every((part) => part !== "" && part !== ".." && part !== ".");
}
/**
 * Something Claude found in the code that the item should name: a screen,
 * setting or behaviour (`name`, product words), why it matters (`why`), and
 * the product area it belongs to when known.
 *
 * `paths` (contract formulator 3, optional): the repository-relative files
 * the finding came from, at most {@link FORMULATOR_FINDING_PATHS_MAX}, for
 * the engineer reading the item later. Never shown as the finding itself; a
 * contract-2 finding has none.
 */
export const formulatorFindingSchema = z.object({
    name: z.string().trim().min(1).max(FORMULATOR_FINDING_NAME_MAX),
    why: z.string().trim().min(1).max(FORMULATOR_FINDING_WHY_MAX),
    area: z.string().trim().min(1).max(FORMULATOR_AREA_NAME_MAX).optional(),
    paths: z
        .array(z
        .string()
        .trim()
        .min(1)
        .max(FORMULATOR_FINDING_PATH_MAX)
        .refine(isFormulatorFindingPath, { message: "path must be relative to the repository root" }))
        .max(FORMULATOR_FINDING_PATHS_MAX)
        .optional(),
});
/** One product area the change touches, and how much (`main`, `small` or `check`). */
export const formulatorAreaTouchSchema = z.object({
    area: z.string().trim().min(1).max(FORMULATOR_AREA_NAME_MAX),
    level: z.enum(FORMULATOR_AREA_LEVELS),
});
/** An open item that may already describe the same thing (opening turn of a create session). */
export const formulatorDuplicateSchema = z.object({
    itemId: formulatorItemIdSchema,
    title: z.string().max(FORMULATOR_DUPLICATE_TITLE_MAX),
    reason: z.string().trim().min(1).max(FORMULATOR_DUPLICATE_REASON_MAX),
});
/** One transcript message. Assistant messages may carry the quick picks offered with them. */
export const formulatorMessageSchema = z.object({
    role: z.enum(FORMULATOR_MESSAGE_ROLES),
    content: z.string(),
    quickPicks: z.array(z.string()).optional(),
});
/**
 * One slot of the readiness rubric the interview fills: its parts (the keys
 * Claude extracts), what each part means, and the current value (null while
 * unfilled). `optional` slots never block completion.
 *
 * Each part carries two texts for two readers. `description` is written for
 * Claude (what to extract and how to format it) and must never be shown to
 * the person. `hint` (optional, added after formulator contract 2 without a
 * number change) is a few plain words for the person, such as "How the team
 * will know it is done", for the Draft panel.
 */
export const formulatorSlotGuideSchema = z.object({
    id: z.string().min(1),
    label: z.string(),
    filled: z.boolean(),
    optional: z.boolean(),
    parts: z.array(z.object({
        key: z.string().min(1),
        /** Instructions for Claude. Never shown to the person. */
        description: z.string(),
        /**
         * Short words for the person, e.g. "How the team will know it is done".
         * Optional: older SpecTree servers send none, so pages fall back to a
         * neutral line rather than to `description`.
         */
        hint: z.string().optional(),
        value: z.string().nullable(),
        /**
         * Contract formulator 4: false while the value was inferred (from the
         * opening description, the title or the item's existing text) and not
         * yet confirmed by the person; absent or true otherwise. An older
         * Toolbox strips it, since part objects are not strict.
         */
        confirmed: z.boolean().optional(),
    })),
});
// ---------------------------------------------------------------------------
// The Claude draft (contract formulator 3)
// ---------------------------------------------------------------------------
/**
 * True when `value` contains markup: a `<` followed by a letter, `/`, `!` or
 * `?` (an HTML tag, closing tag, comment, doctype or processing
 * instruction). A lone `<` as in `a < b` or `<= 5` is plain text.
 */
export function containsMarkup(value) {
    return /<[A-Za-z/!?]/.test(value);
}
/**
 * One draft text: trimmed, non-blank, at most `max` characters, plain text.
 * Paragraphs are separated by a blank line (`\n\n`); SpecTree turns the text
 * into escaped HTML itself. HTML is never accepted.
 */
function draftTextSchema(max) {
    return z
        .string()
        .trim()
        .min(1, "draft text must not be blank")
        .max(max, `draft text must be at most ${String(max)} characters`)
        .refine((value) => !containsMarkup(value), "draft text must be plain text, not HTML");
}
/**
 * One draft list (acceptance criteria, repro steps): 1 to
 * {@link FORMULATOR_DRAFT_ITEMS_MAX} plain-text entries, each on **one line**.
 * A line break inside an entry is refused: the review editor shows a list one
 * entry per line, and ADO shows each entry as one list item anyway.
 */
const draftItemsSchema = z
    .array(draftTextSchema(FORMULATOR_DRAFT_ITEM_MAX).refine((value) => !/[\r\n]/.test(value), "each list entry must be one line"))
    .min(1)
    .max(FORMULATOR_DRAFT_ITEMS_MAX);
/**
 * The basis of a draft: 64 lowercase hex characters (a SHA-256). SpecTree
 * computes it from the session's kind and answers and hands it to the skill
 * in `turn.json` (`draftBasis`); the skill copies it onto the draft
 * unchanged. A draft whose basis no longer matches the answers is stale.
 * The contract checks only the shape; it never computes a hash.
 */
export const formulatorDraftBasisSchema = z
    .string()
    .regex(/^[0-9a-f]{64}$/, "draft basis must be 64 lowercase hex characters");
/**
 * A User Story draft, written by Claude from the complete answers (or edited
 * by the person). All text is plain; paragraphs are separated by a blank
 * line and HTML is never accepted.
 *
 * A **create** draft carries `description` and `acceptanceCriteria`
 * ({@link isCompleteDraft}; SpecTree refuses an incomplete one). An
 * **improve** draft carries only what the interview added, so the item's
 * existing text is never repeated; it needs at least one text field.
 *
 * - `description`: the story text, the user story sentence first.
 * - `acceptanceCriteria`: one testable criterion per entry, each on one line.
 * - `areasNote` (optional): what the code showed about the product areas
 *   affected; SpecTree renders it as a hint, not a requirement.
 * - `basis`: the `draftBasis` the draft was written from.
 */
export const formulatorStoryDraftSchema = z
    .object({
    kind: z.literal("story"),
    description: draftTextSchema(FORMULATOR_DRAFT_TEXT_MAX).optional(),
    acceptanceCriteria: draftItemsSchema.optional(),
    areasNote: draftTextSchema(FORMULATOR_AREAS_NOTE_MAX).optional(),
    basis: formulatorDraftBasisSchema,
})
    .strict()
    .refine((draft) => draft.description !== undefined || draft.acceptanceCriteria !== undefined || draft.areasNote !== undefined, "a draft needs at least one text field");
/**
 * A Bug draft, written by Claude from the complete answers (or edited by the
 * person). All text is plain; paragraphs are separated by a blank line and
 * HTML is never accepted.
 *
 * A **create** draft carries `reproSteps`, `expected` and `actual`
 * ({@link isCompleteDraft}; SpecTree refuses an incomplete one). An
 * **improve** draft carries only what the interview added, so the item's
 * existing text is never repeated; it needs at least one text field.
 *
 * - `reproSteps`: one step per entry, in order, each on one line.
 * - `expected`, `actual`: what should happen and what happens instead.
 * - `frequency` (optional): how often it happens.
 * - `systemInfo` (optional): versions, platform and configuration.
 * - `areasNote` (optional): what the code showed about the product areas
 *   affected; SpecTree renders it as a hint, not a requirement.
 * - `basis`: the `draftBasis` the draft was written from.
 */
export const formulatorBugDraftSchema = z
    .object({
    kind: z.literal("bug"),
    reproSteps: draftItemsSchema.optional(),
    expected: draftTextSchema(FORMULATOR_DRAFT_TEXT_MAX).optional(),
    actual: draftTextSchema(FORMULATOR_DRAFT_TEXT_MAX).optional(),
    frequency: draftTextSchema(FORMULATOR_DRAFT_ITEM_MAX).optional(),
    systemInfo: draftTextSchema(FORMULATOR_DRAFT_TEXT_MAX).optional(),
    areasNote: draftTextSchema(FORMULATOR_AREAS_NOTE_MAX).optional(),
    basis: formulatorDraftBasisSchema,
})
    .strict()
    .refine((draft) => draft.reproSteps !== undefined ||
    draft.expected !== undefined ||
    draft.actual !== undefined ||
    draft.frequency !== undefined ||
    draft.systemInfo !== undefined ||
    draft.areasNote !== undefined, "a draft needs at least one text field");
/**
 * The Claude draft of the final item text, by `kind` (contract formulator
 * 3). Title and severity are not part of it: they still come from the
 * answers.
 */
export const formulatorDraftSchema = z.discriminatedUnion("kind", [formulatorStoryDraftSchema, formulatorBugDraftSchema]);
/**
 * True when `draft` carries every main field of its kind: a story's
 * `description` and `acceptanceCriteria`, a bug's `reproSteps`, `expected`
 * and `actual`. A create-mode draft must be complete; an improve-mode draft
 * carries only what the interview added and may be partial.
 */
export function isCompleteDraft(draft) {
    if (draft.kind === "story")
        return draft.description !== undefined && draft.acceptanceCriteria !== undefined;
    return draft.reproSteps !== undefined && draft.expected !== undefined && draft.actual !== undefined;
}
// ---------------------------------------------------------------------------
// The engine turn (POST /interview/:sessionId/reply)
// ---------------------------------------------------------------------------
/**
 * Slot-part values Claude extracted this turn, keyed by part key. SpecTree
 * keeps only keys that belong to the session's slots and ignores the rest.
 *
 * A {@link boundedRecord}: the key count is checked before any key or value
 * is parsed, so an oversized object yields exactly one issue
 * (GHSA-mrhx-c7xc-m3w5). Accepts exactly the inputs the earlier
 * record-then-refine form accepted.
 */
export const formulatorExtractedSchema = boundedRecord(z.string().min(1).max(FORMULATOR_EXTRACTED_KEY_MAX), z.string().max(FORMULATOR_EXTRACTED_VALUE_MAX), FORMULATOR_EXTRACTED_KEYS_MAX, `extracted holds at most ${String(FORMULATOR_EXTRACTED_KEYS_MAX)} keys`);
/**
 * The engine turn's fields, strict. Kept apart from the exported
 * {@link formulatorTurnBodySchema} because zod refuses `.omit` on a refined
 * object: the marker derives from this object, then both add the same
 * `inferred` check.
 */
const formulatorTurnBodyObject = z
    .object({
    text: z
        .string()
        .min(1, "text must not be empty")
        .max(FORMULATOR_REPLY_MAX, `text must be at most ${String(FORMULATOR_REPLY_MAX)} characters`)
        .refine((value) => value.trim().length > 0, "text must not be blank")
        .optional(),
    extracted: formulatorExtractedSchema,
    question: z.string().trim().min(1).max(FORMULATOR_QUESTION_MAX).nullable().optional(),
    quickPicks: z
        .array(z.string().trim().min(1).max(FORMULATOR_QUICK_PICK_MAX))
        .max(FORMULATOR_QUICK_PICKS_MAX)
        .optional(),
    findings: z.array(formulatorFindingSchema).max(FORMULATOR_FINDINGS_MAX).optional(),
    productAreas: z.array(formulatorAreaTouchSchema).max(FORMULATOR_AREAS_MAX).optional(),
    duplicates: z.array(formulatorDuplicateSchema).max(FORMULATOR_DUPLICATES_MAX).optional(),
    draft: formulatorDraftSchema.optional(),
    inferred: z
        .array(z.string().min(1).max(FORMULATOR_EXTRACTED_KEY_MAX))
        .max(FORMULATOR_EXTRACTED_KEYS_MAX)
        .optional(),
})
    .strict();
/**
 * Every `inferred` entry (contract formulator 4) is a key of the same turn's
 * `extracted` and appears once; an issue at `['inferred', i]` otherwise.
 * Shared by the engine turn and the marker line.
 */
function checkInferredKeys(turn, ctx) {
    if (turn.inferred === undefined)
        return;
    const seen = new Set();
    turn.inferred.forEach((key, index) => {
        if (seen.has(key)) {
            ctx.addIssue({ code: "custom", path: ["inferred", index], message: "inferred keys must be unique" });
        }
        else if (!Object.hasOwn(turn.extracted, key)) {
            ctx.addIssue({ code: "custom", path: ["inferred", index], message: "inferred keys must be keys of extracted" });
        }
        seen.add(key);
    });
}
/**
 * `POST /api/v1/work-items/interview/:sessionId/reply`: one turn the engine
 * produced with Claude Code.
 *
 * - `text`: the person's reply. Absent only on the opening turn (the session
 *   awaits Claude and nobody has answered yet).
 * - `extracted`: slot-part values from the reply and the code.
 * - `question`: the next grounded question, or null when Claude thinks the
 *   draft is complete. SpecTree's rubric decides completion, not Claude.
 * - `quickPicks`, `findings`, `productAreas`: shown with the question;
 *   `findings` and `productAreas` replace the stored lists when sent.
 * - `duplicates`: accepted only on the opening turn of a create session.
 * - `draft` (contract formulator 3, optional): the Claude draft of the final
 *   item text ({@link formulatorDraftSchema}), sent by a draft or redraft
 *   turn. SpecTree accepts it only when the session is complete, the kind
 *   matches and the basis is current. A draft turn may carry no `text` and
 *   an empty `extracted`. A contract-2 turn has none.
 * - `inferred` (contract formulator 4, optional): the part keys of this
 *   turn's `extracted` whose values came from the opening description, the
 *   title or the item's existing text, not from the person's reply or a
 *   quick pick they chose. Each entry must be a key of `extracted`, at most
 *   once. Sent only when `turn.json` carries `confirmInferred`, so an older
 *   SpecTree, whose turn body is strict, never receives it. A contract-3
 *   turn has none.
 */
export const formulatorTurnBodySchema = formulatorTurnBodyObject.superRefine(checkInferredKeys);
/**
 * The JSON after `SPECTREE_FORMULATOR_TURN ` on the skill's result line: the
 * engine turn without the person's text (the engine adds it from
 * `turn.json`), plus the session it answers. The engine refuses a marker for
 * another session (`NO_TURN_RETURNED`). It carries the optional `draft` of
 * the engine turn (contract formulator 3) and the optional `inferred` part
 * keys (contract formulator 4, only when `turn.json` carries
 * `confirmInferred`), checked the same way as on the engine turn.
 */
export const formulatorTurnMarkerSchema = formulatorTurnBodyObject
    .omit({ text: true })
    .extend({ sessionId: z.guid("sessionId must be a UUID") })
    .superRefine(checkInferredKeys);
// ---------------------------------------------------------------------------
// turn.json (engine -> skill)
// ---------------------------------------------------------------------------
/**
 * `turn.json`: what the engine hands the internal skill for one turn. The
 * engine writes it (mode 0600) under the session's run folder; person text
 * goes here, never on the command line.
 *
 * - `opening`: true for the first turn (no reply yet); false for a reply.
 * - `repoPaths`: absolute paths of the read-only repository copies.
 * - `openItemsFile`: absolute path of the open-items list for the duplicate
 *   check (opening turn of a create session only).
 * - `historyFile` (epic PI-SPEC-SW-E316): absolute path of the repository
 *   history file (`history.json`, `toolboxRepoHistoryFileSchema`) the app
 *   wrote with git before the turn, so the skill never runs git. Optional:
 *   an older engine sends none and the schema version stays 1.
 * - `transcript`: the last {@link FORMULATOR_TRANSCRIPT_MAX} messages, so a
 *   fresh Claude run on another machine can pick up the interview.
 * - `persona` (contract formulator 2): the behaviour the interview uses,
 *   copied from the interview view; `qa-tester` asks tester questions.
 *   Absent means `product-owner` (`DEFAULT_TOOLBOX_PERSONA`), so a
 *   contract-1 engine stays valid and the schema version stays 1.
 * - `purpose` (contract formulator 3): what the turn asks for, one of
 *   {@link FORMULATOR_TURN_PURPOSES}. Absent means `interview`, so a
 *   contract-2 engine stays valid and the schema version stays 1. A
 *   `draft` or `redraft` turn needs no `reply`.
 * - `draftBasis` (contract formulator 3): the basis SpecTree computed from
 *   the complete answers ({@link formulatorDraftBasisSchema}); the skill
 *   copies it onto the draft. Required on a `draft` or `redraft` turn.
 * - `currentDraft` (contract formulator 3, `redraft` only): the draft to
 *   rewrite, of the session's kind.
 * - `redraftInstruction` (contract formulator 3, `redraft` only): what the
 *   person asked to change, at most
 *   {@link FORMULATOR_REDRAFT_INSTRUCTION_MAX} characters.
 * - `confirmInferred` (contract formulator 4, `interview` turns only):
 *   `true` when SpecTree asked for it on the interview view; the skill then
 *   reports `inferred` part keys on the marker and asks the person to
 *   confirm inferred values. Absent means a contract-3 turn, whose marker
 *   carries no `inferred`; the schema version stays 1.
 */
export const formulatorTurnInputSchema = z
    .object({
    schema: z.literal(FORMULATOR_TURN_INPUT_SCHEMA_VERSION),
    sessionId: z.guid("sessionId must be a UUID"),
    opening: z.boolean(),
    mode: formulatorModeSchema,
    kind: formulatorKindSchema,
    project: z.string().trim().min(1).max(256),
    itemId: formulatorItemIdSchema.optional(),
    description: z.string().max(FORMULATOR_DESCRIPTION_MAX).optional(),
    reply: z.string().min(1).max(FORMULATOR_REPLY_MAX).optional(),
    repoPaths: z.array(absoluteFilePathSchema).min(1).max(MAX_GROOMING_REPOS),
    openItemsFile: absoluteFilePathSchema.optional(),
    historyFile: absoluteFilePathSchema.optional(),
    slotGuide: z.array(formulatorSlotGuideSchema),
    severityOptions: z.array(z.string()),
    transcript: z
        .array(z.object({ role: z.enum(FORMULATOR_MESSAGE_ROLES), content: z.string() }))
        .max(FORMULATOR_TRANSCRIPT_MAX),
    findings: z.array(formulatorFindingSchema).max(FORMULATOR_FINDINGS_MAX),
    productAreas: z.array(formulatorAreaTouchSchema).max(FORMULATOR_AREAS_MAX),
    persona: z.enum(TOOLBOX_PERSONAS).optional(),
    purpose: z.enum(FORMULATOR_TURN_PURPOSES).optional(),
    draftBasis: formulatorDraftBasisSchema.optional(),
    currentDraft: formulatorDraftSchema.optional(),
    redraftInstruction: z.string().trim().min(1).max(FORMULATOR_REDRAFT_INSTRUCTION_MAX).optional(),
    confirmInferred: z.literal(true).optional(),
})
    .strict()
    .superRefine((input, ctx) => {
    const purpose = input.purpose ?? "interview";
    if (purpose !== "interview" && input.confirmInferred !== undefined) {
        ctx.addIssue({ code: "custom", path: ["confirmInferred"], message: "confirmInferred is sent only on an interview turn" });
    }
    if (purpose === "interview" && !input.opening && input.reply === undefined) {
        ctx.addIssue({ code: "custom", path: ["reply"], message: "a reply turn needs the reply" });
    }
    if (purpose !== "interview" && input.draftBasis === undefined) {
        ctx.addIssue({ code: "custom", path: ["draftBasis"], message: "a draft or redraft turn needs draftBasis" });
    }
    if (purpose === "redraft") {
        if (input.currentDraft === undefined) {
            ctx.addIssue({ code: "custom", path: ["currentDraft"], message: "a redraft turn needs currentDraft" });
        }
        else if (input.currentDraft.kind !== input.kind) {
            ctx.addIssue({ code: "custom", path: ["currentDraft", "kind"], message: "currentDraft must match the session kind" });
        }
        if (input.redraftInstruction === undefined) {
            ctx.addIssue({ code: "custom", path: ["redraftInstruction"], message: "a redraft turn needs redraftInstruction" });
        }
    }
    else {
        if (input.currentDraft !== undefined) {
            ctx.addIssue({ code: "custom", path: ["currentDraft"], message: "currentDraft is sent only on a redraft turn" });
        }
        if (input.redraftInstruction !== undefined) {
            ctx.addIssue({ code: "custom", path: ["redraftInstruction"], message: "redraftInstruction is sent only on a redraft turn" });
        }
    }
    if (input.mode === "improve" && input.itemId === undefined) {
        ctx.addIssue({ code: "custom", path: ["itemId"], message: "improve mode needs itemId" });
    }
});
// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------
/**
 * The part of the API's interview view the engine and the Toolbox pages read.
 * `GET /interview/:sessionId`, `POST /interview/start` and
 * `POST /interview/:sessionId/reply` → `data`. Other fields pass through.
 */
export const formulatorInterviewViewSchema = z
    .object({
    sessionId: z.string(),
    mode: formulatorModeSchema,
    kind: formulatorKindSchema,
    container: z.string(),
    itemId: z.string().nullable(),
    status: formulatorSessionStatusSchema,
    awaiting: formulatorAwaitingSchema,
    complete: z.boolean(),
    /** The question waiting for the person, or null. */
    question: z.string().nullable(),
    slotGuide: z.array(formulatorSlotGuideSchema),
    findings: z.array(formulatorFindingSchema),
    productAreas: z.array(formulatorAreaTouchSchema),
    duplicates: z.array(formulatorDuplicateSchema),
    /** Quick picks offered with the pending question. */
    quickPicks: z.array(z.string()),
    severityOptions: z.array(z.string()),
    messages: z.array(formulatorMessageSchema),
    /** Create mode: what the person typed at the start; null in improve mode. */
    description: z.string().nullable(),
    /**
     * Contract formulator 2: the persona SpecTree stamped on the session at
     * start, which the engine copies into `turn.json`. Null or absent (an
     * older SpecTree or session) means `product-owner`.
     */
    persona: z.enum(TOOLBOX_PERSONAS).nullable().optional(),
    /**
     * Contract formulator 4: `true` when SpecTree wants inferred values
     * confirmed (its view flag is on). The engine then copies it into
     * `turn.json` on interview turns, and only then does the marker carry
     * `inferred`. Absent (an older SpecTree, or the flag off) means the
     * contract-3 behaviour.
     */
    confirmInferred: z.literal(true).optional(),
})
    .loose();
/** One unfinished session in the caller's list. */
export const formulatorSessionSummarySchema = z.object({
    sessionId: z.string(),
    mode: formulatorModeSchema,
    kind: formulatorKindSchema,
    container: z.string(),
    itemId: z.string().nullable(),
    title: z.string(),
    updatedAt: isoDateTimeSchema,
    expiresAt: isoDateTimeSchema,
    complete: z.boolean(),
    awaiting: formulatorAwaitingSchema,
});
/** `GET /interview` → `data`: the caller's active, unexpired sessions, newest first. */
export const formulatorSessionListResponseSchema = z.array(formulatorSessionSummarySchema);
/** One open bug or story, as a duplicate candidate. */
export const openWorkItemSchema = z.object({
    id: z.string(),
    title: z.string(),
    kind: formulatorKindSchema,
    state: z.string(),
    changedDate: isoDateTimeSchema,
    url: z.string(),
});
/** `GET /:tracker/:container/open-items` → `data`. */
export const openWorkItemsResponseSchema = z.object({ items: z.array(openWorkItemSchema) });
//# sourceMappingURL=api.js.map